import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import LeftoverTranchesCard from './LeftoverTranchesCard';
import Ifrs9SettingsCard from './Ifrs9SettingsCard';
import { useAuth } from '../../contexts/AuthContext';
import NewDealDialog from '../../components/admin/NewDealDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Loader2,
  Plus,
  Eye,
  Play,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  DollarSign,
  TrendingUp,
  Users,
  FileText,
  RefreshCw,
  Building2,
  Banknote,
  ArrowRight,
  CreditCard,
  Edit2,
  ArrowLeftRight,
  History
} from 'lucide-react';
import { SortableHeader, useSort } from '../../components/ui/sortable-header';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEAL_STAGES = ['cash', 'supplier', 'transit_from', 'warehouse', 'transit_to', 'delivered'];

const AdminLMSPage = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'deals');
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [investorBalances, setInvestorBalances] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Repayment dialog state
  const [repaymentOpen, setRepaymentOpen] = useState(false);
  const [processingRepayment, setProcessingRepayment] = useState(false);
  const [repaymentStep, setRepaymentStep] = useState('form'); // 'form' | 'review'
  const [repaymentReview, setRepaymentReview] = useState(null);
  const [buildingRepaymentReview, setBuildingRepaymentReview] = useState(false);
  const [repaymentData, setRepaymentData] = useState({
    client_id: '',
    amount: '',
    repayment_type: 'general',
    specific_loan_number: '',
    specific_rp_code: '',
    value_date: new Date().toISOString().split('T')[0],
    bank_reference: '',
    notes: '',
    include_api: true
  });
  const [clientActiveLoans, setClientActiveLoans] = useState([]);
  const [loadingActiveLoans, setLoadingActiveLoans] = useState(false);
  const [reversingBatchId, setReversingBatchId] = useState(null);
  
  // Stage update dialog state
  const [stageUpdateOpen, setStageUpdateOpen] = useState(false);
  const [updatingStage, setUpdatingStage] = useState(false);
  const [stageUpdateDeal, setStageUpdateDeal] = useState(null);
  const [newStage, setNewStage] = useState('');

  // Loan Book Aggregated state
  const [loanBookData, setLoanBookData] = useState([]);
  const [loanBookLoading, setLoanBookLoading] = useState(false);
  const [loanBookSearch, setLoanBookSearch] = useState('');
  const [loanBookStatusFilter, setLoanBookStatusFilter] = useState('all');
  const [rpData, setRpData] = useState([]);
  const [rpStatusFilter, setRpStatusFilter] = useState('active');
  const [rpLoading, setRpLoading] = useState(false);
  const [expandedLoan, setExpandedLoan] = useState(null);
  const { sortConfig: lbSortConfig, handleSort: lbHandleSort, sortItems: lbSortItems } = useSort('loan_number', 'asc');
  
  // IFRS9 Provisioning state
  const [ifrs9Data, setIfrs9Data] = useState(null);
  const [runningIfrs9, setRunningIfrs9] = useState(false);
  const [ifrs9Settings, setIfrs9Settings] = useState(null);
  
  // Repayment history
  const [repaymentHistory, setRepaymentHistory] = useState([]);

  // New Deal (loan_book origination) history
  const [newDealHistory, setNewDealHistory] = useState([]);
  const [reversingNewDealId, setReversingNewDealId] = useState(null);
  
  // Investor Swap state
  const [swapOpen, setSwapOpen] = useState(false);
  const [processingSwap, setProcessingSwap] = useState(false);
  const [swapData, setSwapData] = useState({
    participation_id: '',
    new_investor_id: '',
    swap_amount: '',
    new_rp_rate: '',
    value_date: new Date().toISOString().split('T')[0],
    notes: '',
    carry_enabled: false
  });
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const [swapsHistory, setSwapsHistory] = useState([]);
  
  // Carry Dashboard state
  const [carryDashboard, setCarryDashboard] = useState(null);
  const [loadingCarry, setLoadingCarry] = useState(false);
  const [settlingCarry, setSettlingCarry] = useState(false);
  const [carrySettleDate, setCarrySettleDate] = useState(new Date().toISOString().split('T')[0]);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Handle URL params for deep-linking from alerts
  useEffect(() => {
    const paramStatus = searchParams.get('status');
    const paramIssue = searchParams.get('issue');
    if (activeTab === 'loanbook') {
      if (paramStatus) {
        const filterVal = paramIssue === 'balance' ? `${paramStatus}_with_balance` : paramStatus;
        setLoanBookStatusFilter(filterVal);
        fetchLoanBookData(filterVal);
      } else if (!loanBookData.length) {
        fetchLoanBookData();
      }
    }
    if (activeTab === 'investors') {
      if (paramStatus) {
        const filterVal = paramIssue === 'balance' ? `${paramStatus}_with_balance` : paramStatus;
        setRpStatusFilter(filterVal);
        fetchRPData(filterVal);
      } else if (!rpData.length) {
        fetchRPData();
      }
    }
    if (activeTab === 'repayments' && !repaymentHistory.length) {
      fetchRepaymentHistory();
    }
    if (activeTab === 'deals' && !newDealHistory.length) {
      fetchNewDealHistory();
    }
  }, [activeTab, searchParams]);

  useEffect(() => {
    fetchData();
  }, [token, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, dealsRes, balancesRes, clientsRes, ifrs9Res] = await Promise.all([
        axios.get(`${API_URL}/api/lms/dashboard`, { headers }),
        axios.get(`${API_URL}/api/lms/deals${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, { headers }),
        axios.get(`${API_URL}/api/lms/investors/balances`, { headers }),
        axios.get(`${API_URL}/api/admin/clients`, { headers }),
        axios.get(`${API_URL}/api/lms/ifrs9-provisions`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setDashboard(dashboardRes.data);
      setDeals(dealsRes.data.items || []);
      setInvestorBalances(balancesRes.data.investors || []);
      setClients(Array.isArray(clientsRes.data) ? clientsRes.data : (clientsRes.data.items || []));
      if (ifrs9Res.data.latest_run) {
        setIfrs9Data(ifrs9Res.data.latest_run);
      }
    } catch (err) {
      console.error('Error fetching LMS data:', err);
      toast.error('Failed to load LMS data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDealDetail = async (dealId) => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/deals/${dealId}`, { headers });
      setSelectedDeal(response.data);
      setDetailOpen(true);
    } catch (err) {
      toast.error('Failed to load deal details');
    }
  };

  const fetchLoanBookData = async (statusOverride) => {
    setLoanBookLoading(true);
    try {
      const params = new URLSearchParams();
      const s = statusOverride || loanBookStatusFilter;
      if (s !== 'all') params.append('status', s);
      const res = await axios.get(`${API_URL}/api/lms/loans-aggregated?${params.toString()}`, { headers });
      setLoanBookData(res.data.items || []);
    } catch (err) {
      console.error('Error fetching loan book:', err);
      toast.error('Failed to load loan book data');
    } finally {
      setLoanBookLoading(false);
    }
  };

  const fetchRPData = async (statusOverride) => {
    setRpLoading(true);
    try {
      const params = new URLSearchParams();
      const s = statusOverride || rpStatusFilter;
      if (s !== 'all') params.append('status', s);
      const res = await axios.get(`${API_URL}/api/lms/risk-participations?${params.toString()}`, { headers });
      setRpData(res.data.items || []);
    } catch (err) {
      console.error('Error fetching risk participations:', err);
    } finally {
      setRpLoading(false);
    }
  };

  const fetchNewDealHistory = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/lms/new-deals/history`, { headers });
      setNewDealHistory(r.data.deals || []);
    } catch (err) {
      console.error('Failed to fetch new deal history:', err);
    }
  };

  const handleReverseNewDeal = async (dealId) => {
    if (!window.confirm('Reverse this deal? This deletes the loan tranche(s) entirely (including any fee tranche) and, if capital was drawn via Investec, sends it back to the investor.')) {
      return;
    }
    try {
      setReversingNewDealId(dealId);
      await axios.post(`${API_URL}/api/lms/new-deals/${dealId}/reverse`, {}, { headers });
      toast.success('Deal reversed');
      fetchNewDealHistory();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse deal');
    } finally {
      setReversingNewDealId(null);
    }
  };

  const fetchClientActiveLoans = async (clientId) => {
    if (!clientId) {
      setClientActiveLoans([]);
      return;
    }
    try {
      setLoadingActiveLoans(true);
      const response = await axios.get(
        `${API_URL}/api/lms/repayments/clients/${clientId}/active-loans`,
        { headers }
      );
      setClientActiveLoans(response.data.loans || []);
    } catch (err) {
      setClientActiveLoans([]);
      toast.error(err.response?.data?.detail || 'Failed to load active loans for this client');
    } finally {
      setLoadingActiveLoans(false);
    }
  };

  const fetchRepaymentHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/repayments/history`, { headers });
      setRepaymentHistory(response.data.repayments || []);
    } catch (err) {
      console.error('Failed to fetch repayment history:', err);
    }
  };

  const handleReverseRepaymentBatch = async (batchId) => {
    if (!window.confirm('Reverse this entire repayment batch? This removes the LB/RP entries and, if a real transfer was made, sends the funds back from the investor call account(s).')) {
      return;
    }
    try {
      setReversingBatchId(batchId);
      await axios.post(`${API_URL}/api/lms/repayments/${batchId}/reverse`, {}, { headers });
      toast.success('Repayment batch reversed');
      fetchRepaymentHistory();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse repayment batch');
    } finally {
      setReversingBatchId(null);
    }
  };

  const validateRepaymentForm = () => {
    if (!repaymentData.client_id || !repaymentData.amount || !repaymentData.value_date) {
      toast.error('Please fill in Client, Amount, and Value Date');
      return false;
    }
    if (repaymentData.repayment_type === 'specific_loan' && !repaymentData.specific_loan_number) {
      toast.error('Please select a loan number for this repayment type');
      return false;
    }
    if (repaymentData.repayment_type === 'specific_rp' && !repaymentData.specific_rp_code) {
      toast.error('Please select a risk participant for this repayment type');
      return false;
    }
    return true;
  };

  const repaymentPayload = () => ({
    client_id: repaymentData.client_id,
    amount: parseFloat(repaymentData.amount),
    repayment_type: repaymentData.repayment_type,
    specific_loan_number: repaymentData.specific_loan_number ? parseInt(repaymentData.specific_loan_number, 10) : null,
    specific_rp_code: repaymentData.specific_rp_code || null,
    value_date: repaymentData.value_date,
    bank_reference: repaymentData.bank_reference || null,
    notes: repaymentData.notes || null,
    include_api: repaymentData.include_api
  });

  const resetRepaymentDialog = () => {
    setRepaymentOpen(false);
    setRepaymentStep('form');
    setRepaymentReview(null);
    setRepaymentData({
      client_id: '',
      amount: '',
      repayment_type: 'general',
      specific_loan_number: '',
      specific_rp_code: '',
      value_date: new Date().toISOString().split('T')[0],
      bank_reference: '',
      notes: '',
      include_api: true
    });
    setClientActiveLoans([]);
  };

  const handleReviewRepayment = async () => {
    if (!validateRepaymentForm()) return;
    try {
      setBuildingRepaymentReview(true);
      const response = await axios.post(`${API_URL}/api/lms/repayments/preview`, repaymentPayload(), { headers });
      setRepaymentReview(response.data);
      setRepaymentStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to compute repayment preview');
    } finally {
      setBuildingRepaymentReview(false);
    }
  };

  const handleProcessRepayment = async () => {
    if (!validateRepaymentForm()) return;

    try {
      setProcessingRepayment(true);
      const response = await axios.post(`${API_URL}/api/lms/repayments`, repaymentPayload(), { headers });

      const allocCount = response.data.allocations?.length || 0;
      toast.success(`Repayment processed successfully across ${allocCount} loan(s)`);
      resetRepaymentDialog();
      fetchData();
      fetchRepaymentHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process repayment');
    } finally {
      setProcessingRepayment(false);
    }
  };

  const handleUpdateStage = async () => {
    if (!stageUpdateDeal || !newStage) return;
    
    try {
      setUpdatingStage(true);
      await axios.patch(`${API_URL}/api/lms/deals/${stageUpdateDeal.id}`, { stage: newStage }, { headers });
      toast.success(`Deal ${stageUpdateDeal.loan_number} stage updated to ${newStage}`);
      setStageUpdateOpen(false);
      setStageUpdateDeal(null);
      setNewStage('');
      fetchData();
      if (selectedDeal?.id === stageUpdateDeal.id) {
        fetchDealDetail(stageUpdateDeal.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stage');
    } finally {
      setUpdatingStage(false);
    }
  };

  const openStageUpdateDialog = (deal) => {
    setStageUpdateDeal(deal);
    setNewStage(deal.stage);
    setStageUpdateOpen(true);
  };

  // Stage table: the inputs that run used (older runs: the original defaults).
  const runSet = ifrs9Data?.settings || ifrs9Settings || { stage1_max_days: 30, stage2_max_days: 60, stage1_rate: 0.01, stage2_rate: 0.05, stage3_rate: 0.25, default_rate: 0.25 };
  const runPct = (r) => `${+(Number(r || 0) * 100).toFixed(4)}%`;

  const handleRunIfrs9 = async () => {
    try {
      setRunningIfrs9(true);
      const response = await axios.post(`${API_URL}/api/lms/ifrs9-provisioning`, {}, { headers });
      setIfrs9Data(response.data);
      toast.success(`IFRS9 provisioning completed. Total provision: ${formatCurrency(response.data.total_provision)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run IFRS9 provisioning');
    } finally {
      setRunningIfrs9(false);
    }
  };

  const fetchIfrs9Data = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/ifrs9-provisions`, { headers });
      if (response.data.latest_run) {
        setIfrs9Data(response.data.latest_run);
      }
    } catch (err) {
      console.error('Failed to fetch IFRS9 data:', err);
    }
  };

  const handleActivateDeal = async (dealId) => {
    try {
      await axios.post(`${API_URL}/api/lms/deals/${dealId}/activate`, {}, { headers });
      toast.success('Deal activated');
      fetchData();
      if (selectedDeal?.id === dealId) {
        fetchDealDetail(dealId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to activate deal');
    }
  };

  const handleApproveDeal = async (dealId) => {
    try {
      await axios.post(`${API_URL}/api/lms/deals/${dealId}/approve`, {}, { headers });
      toast.success('Deal approved');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve deal');
    }
  };

  const handleRunAccrual = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/lms/interest-accrual`, {}, { headers });
      toast.success(`Interest accrual completed. ${response.data.participations_updated} participations updated.`);
      fetchData();
    } catch (err) {
      toast.error('Failed to run interest accrual');
    }
  };

  // Investor Swap Handlers
  const openSwapDialog = (participation, deal) => {
    setSelectedParticipation({ ...participation, deal_loan_number: deal?.loan_number });
    setSwapData({
      participation_id: participation.id,
      new_investor_id: '',
      swap_amount: '',
      new_rp_rate: '',
      value_date: new Date().toISOString().split('T')[0],
      notes: '',
      carry_enabled: false
    });
    setSwapOpen(true);
  };

  const handleSwap = async () => {
    if (!swapData.participation_id || !swapData.new_investor_id) {
      toast.error('Please select a new investor');
      return;
    }

    // Validate carry trade conditions
    if (swapData.carry_enabled) {
      const newRate = swapData.new_rp_rate ? parseFloat(swapData.new_rp_rate) : selectedParticipation?.rp_rate;
      if (newRate >= selectedParticipation?.rp_rate) {
        toast.error('For carry trades, the new RP rate must be lower than the original rate');
        return;
      }
    }

    try {
      setProcessingSwap(true);
      const payload = {
        participation_id: swapData.participation_id,
        new_investor_id: swapData.new_investor_id,
        value_date: swapData.value_date,
        notes: swapData.notes || null,
        carry_enabled: swapData.carry_enabled,
      };
      
      // Only include optional fields if they have values
      if (swapData.swap_amount) {
        payload.swap_amount = parseFloat(swapData.swap_amount);
      }
      if (swapData.new_rp_rate) {
        payload.new_rp_rate = parseFloat(swapData.new_rp_rate);
      }

      const response = await axios.post(`${API_URL}/api/lms/swap`, payload, { headers });
      
      let successMsg = `Swap completed: ${response.data.from_investor?.name} → ${response.data.to_investor?.name}`;
      if (response.data.carry_trade?.enabled) {
        successMsg += ` (Carry: ${Number(response.data.carry_trade.carry_rate || 0).toFixed(2)}%)`;
      }
      toast.success(successMsg);
      setSwapOpen(false);
      setSelectedParticipation(null);
      fetchData();
      if (selectedDeal) {
        fetchDealDetail(selectedDeal.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process swap');
    } finally {
      setProcessingSwap(false);
    }
  };

  const fetchSwapsHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/lms/swaps`, { headers });
      setSwapsHistory(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch swaps history:', err);
    }
  };

  // Carry Dashboard functions
  const fetchCarryDashboard = async () => {
    try {
      setLoadingCarry(true);
      const response = await axios.get(`${API_URL}/api/lms/carry/dashboard`, { headers });
      setCarryDashboard(response.data);
    } catch (err) {
      console.error('Failed to fetch carry dashboard:', err);
      toast.error('Failed to load carry dashboard');
    } finally {
      setLoadingCarry(false);
    }
  };

  const accrueCarryInterest = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/lms/carry/accrue`, {}, { headers });
      toast.success(`Accrued carry for ${response.data.count} positions (R${response.data.total_daily_carry?.toFixed(2)} daily)`);
      fetchCarryDashboard();
    } catch (err) {
      toast.error('Failed to accrue carry interest');
    }
  };

  const settleCarry = async (swapId) => {
    try {
      setSettlingCarry(true);
      const response = await axios.post(`${API_URL}/api/lms/carry/settle`, {
        swap_id: swapId,
        value_date: carrySettleDate
      }, { headers });
      toast.success(`Settled R${response.data.settlement_amount?.toFixed(2)} to ${response.data.beneficiary?.name}`);
      fetchCarryDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to settle carry');
    } finally {
      setSettlingCarry(false);
    }
  };

  const settleAllCarry = async () => {
    try {
      setSettlingCarry(true);
      const response = await axios.post(`${API_URL}/api/lms/carry/settle-all?value_date=${carrySettleDate}`, {}, { headers });
      toast.success(`Settled R${response.data.total_settled?.toFixed(2)} across ${response.data.settlements?.length} positions`);
      fetchCarryDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to settle all carry');
    } finally {
      setSettlingCarry(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-amber-100 text-amber-800',
      approved: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      in_arrears: 'bg-red-100 text-red-800',
      defaulted: 'bg-red-200 text-red-900',
      settled: 'bg-emerald-100 text-emerald-800',
      written_off: 'bg-gray-200 text-gray-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  const getStageBadge = (stage) => {
    const variants = {
      cash: 'bg-green-100 text-green-800',
      supplier: 'bg-blue-100 text-blue-800',
      transit_from: 'bg-purple-100 text-purple-800',
      warehouse: 'bg-amber-100 text-amber-800',
      transit_to: 'bg-purple-100 text-purple-800',
      delivered: 'bg-emerald-100 text-emerald-800'
    };
    return variants[stage] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-lms-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#212529] font-['Manrope']">Loan Management System</h1>
          <p className="text-[#6C757D]">Manage deals, participations, and repayments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRunAccrual} data-testid="run-accrual-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Run Interest Accrual
          </Button>
          <Button variant="outline" onClick={() => setRepaymentOpen(true)} data-testid="record-repayment-btn">
            <CreditCard className="h-4 w-4 mr-2" />
            Record Repayment
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#1D2D2A] hover:bg-[#243D37]" data-testid="create-deal-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Deal
          </Button>
        </div>
      </div>

      {/* Sub-R1 leftover balances to mark Inactive (shown only when there are any) */}
      <LeftoverTranchesCard token={token} hideWhenEmpty />

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6C757D]">Active Exposure</p>
                  <p className="text-2xl font-bold text-[#212529]">
                    {formatCurrency(dashboard.total_active_exposure)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6C757D]">Pending Approval</p>
                  <p className="text-2xl font-bold text-amber-600">{dashboard.pending_approval}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6C757D]">Repayments (30d)</p>
                  <p className="text-2xl font-bold text-[#212529]">
                    {formatCurrency(dashboard.recent_repayments_30d?.total_amount)}
                  </p>
                  <p className="text-xs text-[#6C757D]">{dashboard.recent_repayments_30d?.count} transactions</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6C757D]">Investor Funds Available</p>
                  <p className="text-2xl font-bold text-[#212529]">
                    {formatCurrency(dashboard.investor_funds?.total_available)}
                  </p>
                  <p className="text-xs text-[#6C757D]">{dashboard.investor_funds?.investor_count} investors</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); if (v === 'loanbook' && !loanBookData.length) fetchLoanBookData(); }} className="w-full">
        <TabsList className="grid w-full grid-cols-6 max-w-3xl">
          <TabsTrigger value="deals">Deals</TabsTrigger>
          <TabsTrigger value="loanbook" data-testid="tab-loanbook">Loan Book</TabsTrigger>
          <TabsTrigger value="investors">Investors</TabsTrigger>
          <TabsTrigger value="repayments">Repayments</TabsTrigger>
          <TabsTrigger value="carry">Carry</TabsTrigger>
          <TabsTrigger value="ifrs9">IFRS9</TabsTrigger>
        </TabsList>

        {/* Deals Tab */}
        <TabsContent value="deals" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">All Deals</CardTitle>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="in_arrears">In Arrears</SelectItem>
                    <SelectItem value="settled">Settled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Loan #</TableHead>
                      <TableHead>Borrower</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-center">Rate</TableHead>
                      <TableHead className="text-center">Stage</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Investors</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-[#6C757D]">
                          No deals found
                        </TableCell>
                      </TableRow>
                    ) : (
                      deals.map((deal) => (
                        <TableRow key={deal.id}>
                          <TableCell className="font-mono text-sm">{deal.loan_number}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{deal.borrower_name}</p>
                              <p className="text-xs text-[#6C757D]">{deal.borrower_id}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(deal.principal_amount)}
                          </TableCell>
                          <TableCell className="text-center">{deal.interest_rate}%</TableCell>
                          <TableCell className="text-center">
                            <Badge className={getStageBadge(deal.stage)}>{deal.stage}</Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={getStatusBadge(deal.status)}>
                              {deal.status.replace('_', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{deal.investor_count}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => fetchDealDetail(deal.id)}
                                      data-testid={`view-deal-${deal.id}`}
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">View deal details</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              {deal.status === 'active' && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openStageUpdateDialog(deal)}
                                        className="text-purple-600 hover:text-purple-800"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Update loan stage</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {deal.status === 'pending_approval' && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleApproveDeal(deal.id)}
                                        className="text-green-600 hover:text-green-800"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Approve deal</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {deal.status === 'approved' && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleActivateDeal(deal.id)}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        <Play className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top">Activate deal</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">New Deal Ledger Entries</CardTitle>
                  <p className="text-sm text-[#6C757D] mt-1">Loan tranches originated via the New Deal button, written directly to the LoanBook / Risk Participation ledger.</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchNewDealHistory} data-testid="refresh-new-deal-history-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {newDealHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-[#6C757D] text-sm">No deals recorded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Loan ID</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>RP</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-center">Transfer</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newDealHistory.map((h) => (
                      <TableRow key={h.id} className={h.reversed ? 'opacity-50' : ''}>
                        <TableCell className="text-xs">{h.purchase_date}</TableCell>
                        <TableCell className="font-mono text-xs">{h.loan_id}</TableCell>
                        <TableCell className="text-xs">{h.client_id}</TableCell>
                        <TableCell className="text-xs">{h.risk_participant}</TableCell>
                        <TableCell className="text-right">{formatCurrency(h.loan_amount)}</TableCell>
                        <TableCell className="text-center">
                          {h.reversed ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Reversed</Badge>
                          ) : !h.include_api ? (
                            <Badge variant="outline" className="text-xs">Ledger only</Badge>
                          ) : h.transfer_status === 'success' ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Funded</Badge>
                          ) : h.transfer_status === 'partial' ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Partial — needs follow-up</Badge>
                          ) : h.transfer_status === 'skipped_no_mapping' ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">No account mapped</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Failed</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {!h.reversed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={reversingNewDealId === h.id}
                              onClick={() => handleReverseNewDeal(h.id)}
                              data-testid={`reverse-new-deal-${h.id}`}
                            >
                              {reversingNewDealId === h.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reverse'}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>


        {/* Loan Book (Aggregated) Tab */}
        <TabsContent value="loanbook" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Loan Book (Aggregated)</CardTitle>
              <p className="text-sm text-slate-500">Loans grouped by loan number. Click a row to expand tranches.</p>
              <div className="flex items-center gap-4 pt-2">
                <Input
                  placeholder="Search loans..."
                  value={loanBookSearch}
                  onChange={(e) => setLoanBookSearch(e.target.value)}
                  className="max-w-xs"
                  data-testid="loanbook-search"
                />
                <Select value={loanBookStatusFilter} onValueChange={(v) => { setLoanBookStatusFilter(v); fetchLoanBookData(v); }}>
                  <SelectTrigger className="w-48" data-testid="loanbook-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="inactive_with_balance">Inactive with Balance</SelectItem>
                    <SelectItem value="in_arrears">In Arrears</SelectItem>
                    <SelectItem value="settled">Settled</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={fetchLoanBookData} data-testid="loanbook-refresh">
                  <RefreshCw className={`h-4 w-4 ${loanBookLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loanBookLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <SortableHeader label="Loan #" sortKey="loan_number" sortConfig={lbSortConfig} onSort={lbHandleSort} />
                      <SortableHeader label="Borrower" sortKey="client_name" sortConfig={lbSortConfig} onSort={lbHandleSort} />
                      <SortableHeader label="Tranches" sortKey="tranche_count" sortConfig={lbSortConfig} onSort={lbHandleSort} className="text-center" />
                      <SortableHeader label="Total Principal" sortKey="total_principal" sortConfig={lbSortConfig} onSort={lbHandleSort} className="text-right" />
                      <SortableHeader label="Closing Balance" sortKey="total_closing_balance" sortConfig={lbSortConfig} onSort={lbHandleSort} className="text-right" />
                      <SortableHeader label="Avg Rate" sortKey="avg_rate" sortConfig={lbSortConfig} onSort={lbHandleSort} className="text-center" />
                      <SortableHeader label="Status" sortKey="status" sortConfig={lbSortConfig} onSort={lbHandleSort} className="text-center" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lbSortItems(loanBookData
                      .filter(loan => {
                        if (!loanBookSearch) return true;
                        const term = loanBookSearch.toLowerCase();
                        return (
                          String(loan.loan_number).includes(term) ||
                          (loan.client_name || '').toLowerCase().includes(term) ||
                          (loan.client_id || '').toLowerCase().includes(term) ||
                          (loan.customer || '').toLowerCase().includes(term)
                        );
                      }))
                      .map(loan => (
                        <React.Fragment key={loan.loan_number}>
                          <TableRow
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => setExpandedLoan(expandedLoan === loan.loan_number ? null : loan.loan_number)}
                            data-testid={`loanbook-row-${loan.loan_number}`}
                          >
                            <TableCell className="font-mono font-medium">{loan.loan_number}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{loan.client_name || `Client ${loan.client_id}`}</p>
                                <p className="text-xs text-slate-500">{loan.client_id}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{loan.tranche_count}</TableCell>
                            <TableCell className="text-right">{formatCurrency(loan.original_amount)}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(loan.outstanding_balance)}</TableCell>
                            <TableCell className="text-center">{((loan.interest_rate || 0) * 100).toFixed(1)}%</TableCell>
                            <TableCell className="text-center">
                              <Badge className={loan.status === 'active' ? 'bg-green-100 text-green-800' : loan.status === 'in_arrears' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}>
                                {(loan.status || 'unknown').replace('_', ' ')}
                              </Badge>
                            </TableCell>
                          </TableRow>
                          {expandedLoan === loan.loan_number && loan.tranches && (
                            <TableRow>
                              <TableCell colSpan={7} className="bg-slate-50 p-0">
                                <div className="px-8 py-3">
                                  <p className="text-xs font-semibold text-slate-500 mb-2">Tranches for Loan #{loan.loan_number}</p>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead className="text-xs">Tranche</TableHead>
                                        <TableHead className="text-xs">Loan ID</TableHead>
                                        <TableHead className="text-xs text-right">Amount</TableHead>
                                        <TableHead className="text-xs text-right">Balance</TableHead>
                                        <TableHead className="text-xs text-center">Rate</TableHead>
                                        <TableHead className="text-xs text-center">Status</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {loan.tranches.map((t, idx) => (
                                        <TableRow key={idx} className="text-sm">
                                          <TableCell>{t.tranche || idx + 1}</TableCell>
                                          <TableCell className="font-mono text-xs">{t.loan_id}</TableCell>
                                          <TableCell className="text-right">{formatCurrency(t.amount)}</TableCell>
                                          <TableCell className="text-right">{formatCurrency(t.balance)}</TableCell>
                                          <TableCell className="text-center">{((t.interest_rate || 0) * 100).toFixed(1)}%</TableCell>
                                          <TableCell className="text-center">
                                            <Badge variant="outline" className={t.status === 'active' ? 'text-green-700' : 'text-gray-500'}>
                                              {(t.status || '-').replace('_', ' ')}
                                            </Badge>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    {loanBookData.length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No loans found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Investors Tab */}
        <TabsContent value="investors" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Risk Participations</CardTitle>
                  <p className="text-sm text-[#6C757D] mt-1">All investor risk participation records</p>
                </div>
                <Select value={rpStatusFilter} onValueChange={(v) => { setRpStatusFilter(v); fetchRPData(v); }}>
                  <SelectTrigger className="w-48" data-testid="rp-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="inactive_with_balance">Inactive with Balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {rpLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : rpData.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{rpStatusFilter !== 'all' ? `No ${rpStatusFilter.replace(/_/g, ' ')} risk participations found` : 'No risk participations loaded. Change filter to load data.'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <p className="text-sm text-gray-500 mb-3">{rpData.length} record(s)</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>RP Code</TableHead>
                        <TableHead>Investor</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Loan</TableHead>
                        <TableHead className="text-right">Amount (ZAR)</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rpData.map((rp, idx) => {
                        const hasIssue = (rp.status === 'inactive' || rp.status === 'closed' || rp.status === 'settled') && (rp.closing_balance || 0) > 0.01;
                        return (
                          <TableRow key={rp.id || idx} className={hasIssue ? 'bg-red-50' : ''}>
                            <TableCell className="font-mono text-sm">{rp.risk_participant}</TableCell>
                            <TableCell>
                              <div>
                                <p className="text-sm">{rp.investor_name || rp.investor_id || '-'}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{rp.client_id || '-'}</TableCell>
                            <TableCell className="text-sm">{rp.loan_number || '-'}</TableCell>
                            <TableCell className="text-right text-sm">{formatCurrency(rp.loan_amount_zar)}</TableCell>
                            <TableCell className={`text-right text-sm font-medium ${hasIssue ? 'text-red-600' : ''}`}>
                              {formatCurrency(rp.closing_balance)}
                              {hasIssue && <span className="ml-1 text-xs text-red-500">!</span>}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={
                                rp.status === 'active' ? 'bg-green-100 text-green-800' :
                                rp.status === 'inactive' ? 'bg-gray-100 text-gray-600' :
                                'bg-amber-100 text-amber-800'
                              }>{rp.status}</Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Investor Call Account Balances (Mock) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Investor Call Account Balances</CardTitle>
              <p className="text-sm text-[#6C757D]">Mock data - will be replaced with Investec API</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Investor</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Committed</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investorBalances.map((inv) => (
                      <TableRow key={inv.investor_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inv.investor_name}</p>
                            <p className="text-xs text-[#6C757D]">{inv.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.balance)}</TableCell>
                        <TableCell className="text-right text-amber-600">{formatCurrency(inv.committed)}</TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          {formatCurrency(inv.available)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Repayments Tab */}
        <TabsContent value="repayments" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Repayment Processing</CardTitle>
                  <p className="text-sm text-[#6C757D] mt-1">Process and track client repayments with different allocation methods</p>
                </div>
                <Button onClick={() => setRepaymentOpen(true)} size="sm" className="bg-[#1D2D2A] hover:bg-[#243D37]">
                  <Plus className="h-4 w-4 mr-2" />
                  Record Repayment
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-blue-800">General (FIFO)</span>
                  </div>
                  <p className="text-xs text-blue-600">Allocate to oldest loans first, then next oldest, etc.</p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <TrendingUp className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium text-purple-800">Interest Pro-rata</span>
                  </div>
                  <p className="text-xs text-purple-600">Distribute proportionally based on accrued interest.</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="text-sm font-medium text-amber-800">Specific Loan</span>
                  </div>
                  <p className="text-xs text-amber-600">Apply entire payment to one specific loan only.</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium text-sm mb-3">Waterfall Priority</h4>
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">1. Penalties</Badge>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">2. Fees</Badge>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">3. Interest</Badge>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">4. Principal</Badge>
                </div>
                <p className="text-xs text-[#6C757D] mt-2">
                  All repayments follow this waterfall: penalties are paid first, then fees, then interest, and finally principal reduction.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Repayment History</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchRepaymentHistory} data-testid="refresh-repayment-history-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {repaymentHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-[#6C757D] text-sm">No repayments recorded yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reference</TableHead>
                      <TableHead>Loan</TableHead>
                      <TableHead>RP</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">LB repayment</TableHead>
                      <TableHead className="text-right">RP repayment</TableHead>
                      <TableHead className="text-center">Transfer</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {repaymentHistory.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono text-xs">{r.reference}</TableCell>
                        <TableCell className="font-mono text-xs">{r.loan_id}</TableCell>
                        <TableCell className="text-xs">{r.risk_participant || '-'}</TableCell>
                        <TableCell className="text-xs">{r.repayment_date}</TableCell>
                        {/* Both legs as cashflows (money off the loan = negative), 2026-10-15 */}
                        <TableCell className="text-right font-mono">{formatCurrency(r.lb_amount != null ? r.lb_amount : -Math.abs(Number(r.amount_applied) || 0))}</TableCell>
                        <TableCell className="text-right font-mono" data-testid={`repayment-rp-${r.id}`}>{r.rp_amount != null ? formatCurrency(r.rp_amount) : '—'}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs">{r.transfer_status}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {r.reversed ? (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Reversed</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Active</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {!r.reversed && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={reversingBatchId === r.batch_id}
                              onClick={() => handleReverseRepaymentBatch(r.batch_id)}
                              data-testid={`reverse-repayment-${r.id}`}
                            >
                              {reversingBatchId === r.batch_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Reverse'
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Carry Dashboard Tab */}
        <TabsContent value="carry" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                    Carry Dashboard
                  </CardTitle>
                  <p className="text-sm text-[#6C757D] mt-1">
                    Track and settle carry payments to original investors from swaps
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={fetchCarryDashboard}
                    disabled={loadingCarry}
                    data-testid="refresh-carry-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingCarry ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={accrueCarryInterest}
                    className="border-green-200 text-green-700 hover:bg-green-50"
                    data-testid="accrue-carry-btn"
                  >
                    <TrendingUp className="h-4 w-4 mr-2" />
                    Accrue Interest
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!carryDashboard ? (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-[#6C757D]">Click Refresh to load carry positions</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={fetchCarryDashboard}
                  >
                    Load Dashboard
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Grand Totals Summary */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-[#6C757D] uppercase tracking-wider">Active Principal</p>
                      <p className="text-xl font-bold text-slate-700 mt-1">
                        {formatCurrency(carryDashboard.grand_totals?.principal)}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-green-600 uppercase tracking-wider">Total Accrued</p>
                      <p className="text-xl font-bold text-green-700 mt-1">
                        {formatCurrency(carryDashboard.grand_totals?.accrued)}
                      </p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-blue-600 uppercase tracking-wider">Total Paid</p>
                      <p className="text-xl font-bold text-blue-700 mt-1">
                        {formatCurrency(carryDashboard.grand_totals?.paid)}
                      </p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 text-center">
                      <p className="text-xs text-amber-600 uppercase tracking-wider">Outstanding</p>
                      <p className="text-xl font-bold text-amber-700 mt-1">
                        {formatCurrency(carryDashboard.grand_totals?.outstanding)}
                      </p>
                    </div>
                  </div>

                  {/* Settle All Section */}
                  {carryDashboard.grand_totals?.outstanding > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-amber-800">Outstanding Carry to Settle</p>
                          <p className="text-sm text-amber-600">
                            {carryDashboard.total_beneficiaries} beneficiaries with {formatCurrency(carryDashboard.grand_totals?.outstanding)} outstanding
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input
                            type="date"
                            value={carrySettleDate}
                            onChange={(e) => setCarrySettleDate(e.target.value)}
                            className="w-40"
                            data-testid="carry-settle-date"
                          />
                          <Button 
                            onClick={settleAllCarry}
                            disabled={settlingCarry}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="settle-all-carry-btn"
                          >
                            {settlingCarry ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Banknote className="h-4 w-4 mr-2" />
                            )}
                            Settle All
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Beneficiaries List */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-[#6C757D] uppercase tracking-wider">
                      Carry Beneficiaries ({carryDashboard.total_beneficiaries})
                    </h4>
                    
                    {carryDashboard.beneficiaries?.length === 0 ? (
                      <div className="text-center py-8 bg-slate-50 rounded-lg">
                        <p className="text-[#6C757D]">No carry positions found</p>
                        <p className="text-sm text-[#6C757D] mt-1">Create a swap with carry enabled to track carry earnings</p>
                      </div>
                    ) : (
                      carryDashboard.beneficiaries?.map((beneficiary) => (
                        <Card key={beneficiary.investor_id} className="border-l-4 border-l-green-500">
                          <CardContent className="pt-4">
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <h5 className="font-medium">{beneficiary.investor_name}</h5>
                                <p className="text-xs text-[#6C757D]">
                                  {beneficiary.active_positions} active position(s) • 
                                  Principal: {formatCurrency(beneficiary.total_principal)}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-amber-600">
                                  {formatCurrency(beneficiary.total_outstanding)}
                                </p>
                                <p className="text-xs text-[#6C757D]">outstanding</p>
                              </div>
                            </div>
                            
                            {/* Positions Table */}
                            <Table>
                              <TableHeader>
                                <TableRow className="text-xs">
                                  <TableHead>Loan</TableHead>
                                  <TableHead>Sold To</TableHead>
                                  <TableHead className="text-center">Carry Rate</TableHead>
                                  <TableHead className="text-right">Principal</TableHead>
                                  <TableHead className="text-right">Accrued</TableHead>
                                  <TableHead className="text-right">Paid</TableHead>
                                  <TableHead className="text-right">Outstanding</TableHead>
                                  <TableHead className="text-center">Status</TableHead>
                                  <TableHead className="text-center">Action</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {beneficiary.positions?.map((pos) => (
                                  <TableRow key={pos.swap_id} className="text-sm">
                                    <TableCell className="font-mono text-xs">{pos.loan_number}</TableCell>
                                    <TableCell>{pos.to_investor_name}</TableCell>
                                    <TableCell className="text-center">
                                      <Badge variant="outline" className="bg-green-50 text-green-700">
                                        {Number(pos.carry_rate || 0).toFixed(2)}%
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">{formatCurrency(pos.principal)}</TableCell>
                                    <TableCell className="text-right text-green-600">{formatCurrency(pos.accrued)}</TableCell>
                                    <TableCell className="text-right text-blue-600">{formatCurrency(pos.paid)}</TableCell>
                                    <TableCell className="text-right font-medium text-amber-600">
                                      {formatCurrency(pos.outstanding)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      <Badge variant={pos.status === 'active' ? 'default' : 'secondary'}>
                                        {pos.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                      {pos.outstanding > 0 && pos.status === 'active' && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => settleCarry(pos.swap_id)}
                                          disabled={settlingCarry}
                                          className="h-7 text-xs"
                                          data-testid={`settle-carry-${pos.swap_id}`}
                                        >
                                          <Banknote className="h-3 w-3 mr-1" />
                                          Settle
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            
                            {/* Beneficiary Totals */}
                            <div className="flex justify-end gap-6 mt-3 pt-3 border-t text-sm">
                              <span className="text-[#6C757D]">
                                Accrued: <span className="font-medium text-green-600">{formatCurrency(beneficiary.total_accrued)}</span>
                              </span>
                              <span className="text-[#6C757D]">
                                Paid: <span className="font-medium text-blue-600">{formatCurrency(beneficiary.total_paid)}</span>
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* IFRS9 Provisioning Tab */}
        <TabsContent value="ifrs9" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    IFRS9 Expected Credit Loss Provisioning
                  </CardTitle>
                  <p className="text-sm text-[#6C757D] mt-1">
                    Calculate ECL provisions based on loan staging and days past due
                  </p>
                </div>
                <Button 
                  onClick={handleRunIfrs9} 
                  disabled={runningIfrs9}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="run-ifrs9-btn"
                >
                  {runningIfrs9 ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Calculating...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Run IFRS9 Calculation
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* IFRS9 inputs (editable) + loans in default */}
              <Ifrs9SettingsCard token={token} onSettingsChange={setIfrs9Settings} />

              {/* Latest Provision Results */}
              {ifrs9Data ? (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-sm mb-4">Latest Provision Run - {ifrs9Data.run_date}</h4>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-[#6C757D]">Deals Assessed</p>
                      <p className="text-xl font-bold text-[#212529]">{ifrs9Data.deals_assessed || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-[#6C757D]">Total Exposure</p>
                      <p className="text-xl font-bold text-[#212529]">{formatCurrency(ifrs9Data.total_exposure || 0)}</p>
                    </div>
                    <div className="bg-red-50 p-3 rounded-lg">
                      <p className="text-xs text-red-600">Total Provision</p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(ifrs9Data.total_provision || 0)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-[#6C757D]">Coverage Ratio</p>
                      <p className="text-xl font-bold text-[#212529]">
                        {ifrs9Data.total_exposure > 0 
                          ? ((ifrs9Data.total_provision / ifrs9Data.total_exposure) * 100).toFixed(2)
                          : 0}%
                      </p>
                    </div>
                  </div>

                  {/* Stage Breakdown */}
                  {ifrs9Data.provisions_by_stage && (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Stage</TableHead>
                            <TableHead className="text-center">Deals</TableHead>
                            <TableHead className="text-right">Exposure</TableHead>
                            <TableHead className="text-center">Rate</TableHead>
                            <TableHead className="text-right">Provision</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-green-500">1</Badge>
                                <span>Performing (0-{runSet.stage1_max_days} days)</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{ifrs9Data.provisions_by_stage["1"]?.count || 0}</TableCell>
                            <TableCell className="text-right">{formatCurrency(ifrs9Data.provisions_by_stage["1"]?.exposure || 0)}</TableCell>
                            <TableCell className="text-center">{runPct(runSet.stage1_rate)}</TableCell>
                            <TableCell className="text-right font-medium text-green-700">{formatCurrency(ifrs9Data.provisions_by_stage["1"]?.provision || 0)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-amber-500">2</Badge>
                                <span>Underperforming ({runSet.stage1_max_days + 1}-{runSet.stage2_max_days} days)</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{ifrs9Data.provisions_by_stage["2"]?.count || 0}</TableCell>
                            <TableCell className="text-right">{formatCurrency(ifrs9Data.provisions_by_stage["2"]?.exposure || 0)}</TableCell>
                            <TableCell className="text-center">{runPct(runSet.stage2_rate)}</TableCell>
                            <TableCell className="text-right font-medium text-amber-700">{formatCurrency(ifrs9Data.provisions_by_stage["2"]?.provision || 0)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-red-500">3</Badge>
                                <span>Non-performing ({runSet.stage2_max_days}+ days) / in default</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{ifrs9Data.provisions_by_stage["3"]?.count || 0}</TableCell>
                            <TableCell className="text-right">{formatCurrency(ifrs9Data.provisions_by_stage["3"]?.exposure || 0)}</TableCell>
                            <TableCell className="text-center">{runPct(runSet.stage3_rate)}{Number(runSet.default_rate) !== Number(runSet.stage3_rate) ? ` / ${runPct(runSet.default_rate)}` : ''}</TableCell>
                            <TableCell className="text-right font-medium text-red-700">{formatCurrency(ifrs9Data.provisions_by_stage["3"]?.provision || 0)}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="border-t pt-4 text-center py-8">
                  <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#6C757D]">No IFRS9 provisioning data available.</p>
                  <p className="text-sm text-[#6C757D] mt-1">Click "Run IFRS9 Calculation" to generate provisions.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <NewDealDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        clients={clients.filter(c => c.is_active !== false) /* inactive clients: no new deals */}
        onCreated={() => { fetchData(); fetchNewDealHistory(); }}
      />

      {/* Deal Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedDeal?.loan_number}
              <Badge className={getStatusBadge(selectedDeal?.status)}>
                {selectedDeal?.status?.replace('_', ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedDeal && (
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="participations">Participations</TabsTrigger>
                <TabsTrigger value="transactions">Transactions</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Borrower</span>
                          <span className="font-medium">{selectedDeal.borrower_name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Principal</span>
                          <span className="font-medium">{formatCurrency(selectedDeal.principal_amount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Interest Rate</span>
                          <span className="font-medium">{selectedDeal.interest_rate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Expected Interest</span>
                          <span className="font-medium">{formatCurrency(selectedDeal.expected_interest)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Stage</span>
                          <Badge className={getStageBadge(selectedDeal.stage)}>{selectedDeal.stage}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Purchase Date</span>
                          <span className="font-medium">{selectedDeal.purchase_date}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Repurchase Date</span>
                          <span className="font-medium">{selectedDeal.repurchase_date}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-[#6C757D]">Term</span>
                          <span className="font-medium">{selectedDeal.term_days} days</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {selectedDeal.status === 'approved' && (
                  <Button
                    onClick={() => handleActivateDeal(selectedDeal.id)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Activate Deal (Disburse Funds)
                  </Button>
                )}
              </TabsContent>

              <TabsContent value="participations" className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Participation</TableHead>
                      <TableHead>Investor</TableHead>
                      <TableHead className="text-right">Allocated</TableHead>
                      <TableHead className="text-right">Outstanding</TableHead>
                      <TableHead className="text-right">Interest Accrued</TableHead>
                      <TableHead className="text-center">RP Rate</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedDeal.participations?.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-mono text-xs">{p.participation_number || '-'}</TableCell>
                        <TableCell className="font-medium">{p.investor_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(p.allocated_amount)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(p.principal_outstanding)}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(p.interest_accrued)}
                        </TableCell>
                        <TableCell className="text-center">{p.rp_rate}%</TableCell>
                        <TableCell className="text-center">
                          {p.status === 'active' && p.principal_outstanding > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openSwapDialog(p, selectedDeal)}
                              className="h-7 text-xs"
                              data-testid={`swap-btn-${p.id}`}
                            >
                              <ArrowLeftRight className="h-3 w-3 mr-1" />
                              Swap
                            </Button>
                          )}
                          {p.swap_history?.length > 0 && (
                            <Badge variant="outline" className="ml-1 text-xs">
                              <History className="h-3 w-3 mr-1" />
                              {p.swap_history.length}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>

              <TabsContent value="transactions" className="pt-4">
                {selectedDeal.transactions?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead>Reference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedDeal.transactions?.map((t) => (
                        <TableRow key={t.id}>
                          <TableCell>{t.transaction_date}</TableCell>
                          <TableCell className="capitalize">{t.transaction_type}</TableCell>
                          <TableCell className="text-right">{formatCurrency(t.amount)}</TableCell>
                          <TableCell className="font-mono text-sm">{t.reference}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-[#6C757D] py-8">No transactions yet</p>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Repayment Dialog */}
      <Dialog open={repaymentOpen} onOpenChange={(open) => { if (open) setRepaymentOpen(true); else resetRepaymentDialog(); }}>
        {/* Bounded to the screen (2026-10-14): long content scrolls inside,
            the buttons stay visible at the bottom. */}
        <DialogContent className="max-w-xl w-[calc(100vw-1.5rem)] max-h-[90dvh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              {repaymentStep === 'review' ? 'Review Repayment' : 'Record Repayment'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mx-1 px-1">
          {repaymentStep === 'review' ? (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-3 rounded-md text-sm">
                <span className="text-[#6C757D]">Requested:</span>{' '}
                <span className="font-medium">{formatCurrency(parseFloat(repaymentData.amount) || 0)}</span>
                {' · '}
                <span className="text-[#6C757D]">Type:</span>{' '}
                <span className="font-medium">{repaymentData.repayment_type}</span>
              </div>
              <div className="border rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Loan</TableHead>
                      <TableHead className="text-xs">RP</TableHead>
                      <TableHead className="text-xs text-right">LB repayment</TableHead>
                      <TableHead className="text-xs text-right">RP repayment</TableHead>
                      <TableHead className="text-xs text-right">LB balance before → after</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(repaymentReview?.allocations || []).map((a, idx) => (
                      <TableRow key={a.loan_id + idx}>
                        <TableCell className="text-xs font-mono">{a.loan_id}</TableCell>
                        <TableCell className="text-xs">{a.risk_participant || '—'}</TableCell>
                        {/* Both repayments shown as cashflows (money coming off the loan = negative). */}
                        <TableCell className="text-xs text-right font-mono">{formatCurrency(-Math.abs(Number(a.amount_applied) || 0))}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{formatCurrency(-Math.abs(Number(a.rp_amount) || 0))}</TableCell>
                        <TableCell className="text-xs text-right font-mono whitespace-nowrap">
                          {formatCurrency(a.lb_closing_balance_before)} → {formatCurrency(a.lb_closing_balance_after)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-[#6C757D]">
                LB repayment = what comes off the client's loan. RP repayment = what comes off the investor's tranche. The RP figure can be smaller:
                any gap between the LB and RP balances (from the different rates) is closed first.
              </p>
              {(() => {
                const totalApplied = (repaymentReview?.allocations || []).reduce((s, a) => s + (a.amount_applied || 0), 0);
                const requested = parseFloat(repaymentData.amount) || 0;
                const shortfall = requested - totalApplied;
                if (shortfall > 0.01) {
                  return (
                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-md text-xs text-amber-800">
                      Only {formatCurrency(totalApplied)} of {formatCurrency(requested)} will be applied —
                      {' '}{formatCurrency(shortfall)} has nowhere to go under this repayment type/selection.
                    </div>
                  );
                }
                return (
                  <div className="bg-green-50 border border-green-200 p-3 rounded-md text-xs text-green-800">
                    Full amount will be applied across {repaymentReview?.allocations?.length || 0} loan(s).
                  </div>
                );
              })()}
              {repaymentData.include_api && (
                <p className="text-xs text-[#6C757D]">
                  Investec transfers will be attempted for each loan above with a non-zero RP amount once confirmed.
                </p>
              )}
            </div>
          ) : (
          <div className="grid gap-4 py-4">
            <div>
              <Label>Client/Borrower *</Label>
              <Select
                value={repaymentData.client_id}
                onValueChange={(v) => {
                  setRepaymentData({ ...repaymentData, client_id: v, specific_loan_number: '', specific_rp_code: '' });
                  fetchClientActiveLoans(v);
                }}
              >
                <SelectTrigger data-testid="repayment-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_active !== false).map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>
                      {client.company_name || client.client_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount (ZAR) *</Label>
                <Input
                  type="number"
                  value={repaymentData.amount}
                  onChange={(e) => setRepaymentData({ ...repaymentData, amount: e.target.value })}
                  placeholder="50000"
                  data-testid="repayment-amount-input"
                />
              </div>
              <div>
                <Label>Value Date *</Label>
                <Input
                  type="date"
                  value={repaymentData.value_date}
                  onChange={(e) => setRepaymentData({ ...repaymentData, value_date: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Repayment Type *</Label>
              <Select
                value={repaymentData.repayment_type}
                onValueChange={(v) => setRepaymentData({ ...repaymentData, repayment_type: v })}
              >
                <SelectTrigger data-testid="repayment-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">
                    <div className="flex flex-col">
                      <span className="font-medium">General (FIFO)</span>
                      <span className="text-xs text-[#6C757D]">Allocate to oldest loans first</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="interest">
                    <div className="flex flex-col">
                      <span className="font-medium">Interest Pro-rata</span>
                      <span className="text-xs text-[#6C757D]">Distribute by accrued interest</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="specific_loan">
                    <div className="flex flex-col">
                      <span className="font-medium">Specific Loan</span>
                      <span className="text-xs text-[#6C757D]">Apply to one deal (Loan Number): shared across its tranches in proportion to their balances</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="specific_rp">
                    <div className="flex flex-col">
                      <span className="font-medium">Specific RP</span>
                      <span className="text-xs text-[#6C757D]">Apply to this client's loan under one risk participant</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {repaymentData.repayment_type === 'specific_loan' && (
              <div>
                <Label>Loan Number (deal) *</Label>
                <Select
                  value={repaymentData.specific_loan_number ? String(repaymentData.specific_loan_number) : ''}
                  onValueChange={(v) => setRepaymentData({ ...repaymentData, specific_loan_number: v })}
                >
                  <SelectTrigger className="w-full min-w-0" data-testid="specific-loan-select">
                    <SelectValue placeholder={loadingActiveLoans ? 'Loading loans...' : 'Select loan number'} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Grouped by loan_number, not loan_id: a deal with more than
                        one investor has one loan_book tranche per RP sharing the
                        same loan_number - this mode targets the whole deal, so
                        the amount is applied across every tranche under it. */}
                    {Object.values(clientActiveLoans.reduce((acc, l) => {
                      const key = l.loan_number;
                      if (!acc[key]) acc[key] = { loan_number: key, total: 0, tranches: [] };
                      acc[key].total += Number(l.closing_balance || 0);
                      acc[key].tranches.push(l);
                      return acc;
                    }, {})).sort((a, b) => a.loan_number - b.loan_number).map((deal) => (
                      <SelectItem key={deal.loan_number} value={String(deal.loan_number)}>
                        Loan {deal.loan_number} · {formatCurrency(deal.total)} · {deal.tranches.length} tranche{deal.tranches.length === 1 ? '' : 's'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {repaymentData.specific_loan_number && (() => {
                  // The RP tranches of the selected loan, wrapped below the box
                  // rather than inside it (they used to stretch the window).
                  const tr = clientActiveLoans.filter(l => String(l.loan_number) === String(repaymentData.specific_loan_number));
                  return tr.length > 0 && (
                    <p className="text-xs text-[#6C757D] mt-1 break-words" data-testid="specific-loan-tranches">
                      RP tranches: {tr.map(t => `${t.risk_participant} ${formatCurrency(t.closing_balance)}`).join(' · ')}
                    </p>
                  );
                })()}
                {repaymentData.client_id && !loadingActiveLoans && clientActiveLoans.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No active loans for this client</p>
                )}
              </div>
            )}

            {repaymentData.repayment_type === 'specific_rp' && (
              <div>
                <Label>Risk Participant *</Label>
                <Select
                  value={repaymentData.specific_rp_code}
                  onValueChange={(v) => setRepaymentData({ ...repaymentData, specific_rp_code: v })}
                >
                  <SelectTrigger className="w-full min-w-0" data-testid="specific-rp-select">
                    <SelectValue placeholder={loadingActiveLoans ? 'Loading...' : 'Select risk participant'} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Cumulative exposure across every loan/tranche this RP
                        holds against this client, not one arbitrary tranche's
                        balance - the backend already sums across all of them
                        for this mode, so the dropdown should show the same total. */}
                    {Object.values(clientActiveLoans.reduce((acc, l) => {
                      const key = l.risk_participant;
                      if (!acc[key]) acc[key] = { risk_participant: key, total: 0, loans: [] };
                      acc[key].total += Number(l.closing_balance || 0);
                      acc[key].loans.push(l);
                      return acc;
                    }, {})).sort((a, b) => a.risk_participant.localeCompare(b.risk_participant)).map((rp) => (
                      <SelectItem key={rp.risk_participant} value={rp.risk_participant}>
                        {rp.risk_participant} - {formatCurrency(rp.total)} total across {rp.loans.length} loan{rp.loans.length === 1 ? '' : 's'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {repaymentData.client_id && !loadingActiveLoans && clientActiveLoans.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">No active loans for this client</p>
                )}
              </div>
            )}

            <div>
              <Label>Bank Reference</Label>
              <Input
                value={repaymentData.bank_reference}
                onChange={(e) => setRepaymentData({ ...repaymentData, bank_reference: e.target.value })}
                placeholder="e.g., INV-TXN-12345"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={repaymentData.notes}
                onChange={(e) => setRepaymentData({ ...repaymentData, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between bg-slate-50 p-3 rounded-md border">
              <div>
                <Label className="text-sm">Push repayment to Investec</Label>
                <p className="text-xs text-[#6C757D] mt-0.5">
                  Sends the RP-leg amount from the fund's disbursement account to each investor's call account. Turn off if the bank transfer was already made manually.
                </p>
              </div>
              <Switch
                checked={repaymentData.include_api}
                onCheckedChange={(v) => setRepaymentData({ ...repaymentData, include_api: v })}
                data-testid="repayment-include-api-switch"
              />
            </div>

            <div className="bg-blue-50 p-3 rounded-md">
              <h4 className="font-medium text-blue-800 text-sm mb-1">LB-RP Allocation</h4>
              <p className="text-xs text-blue-600">
                Repayments write directly to the loan book and risk participation ledgers, closing any pre-existing LB-RP gap first before reducing the investor's balance.
              </p>
            </div>
          </div>
          )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-3">
            {repaymentStep === 'review' ? (
              <>
                <Button variant="outline" onClick={() => setRepaymentStep('form')}>Back</Button>
                <Button
                  onClick={handleProcessRepayment}
                  disabled={processingRepayment}
                  className="bg-[#1D2D2A] hover:bg-[#243D37]"
                  data-testid="confirm-repayment-btn"
                >
                  {processingRepayment ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Confirm & Process'
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={resetRepaymentDialog}>Cancel</Button>
                <Button
                  onClick={handleReviewRepayment}
                  disabled={buildingRepaymentReview}
                  className="bg-[#1D2D2A] hover:bg-[#243D37]"
                  data-testid="review-repayment-btn"
                >
                  {buildingRepaymentReview ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Preparing review…
                    </>
                  ) : (
                    'Review'
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Stage Dialog */}
      <Dialog open={stageUpdateOpen} onOpenChange={setStageUpdateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5" />
              Update Deal Stage
            </DialogTitle>
          </DialogHeader>
          {stageUpdateDeal && (
            <div className="py-4 space-y-4">
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-[#6C757D]">Deal</p>
                <p className="font-mono font-medium">{stageUpdateDeal.loan_number}</p>
                <p className="text-sm text-[#6C757D] mt-1">Borrower: {stageUpdateDeal.borrower_name}</p>
              </div>
              
              <div>
                <Label>Current Stage</Label>
                <div className="mt-1">
                  <Badge className={getStageBadge(stageUpdateDeal.stage)}>
                    {stageUpdateDeal.stage.replace('_', ' ')}
                  </Badge>
                </div>
              </div>
              
              <div>
                <Label>New Stage</Label>
                <Select value={newStage} onValueChange={setNewStage}>
                  <SelectTrigger data-testid="new-stage-select">
                    <SelectValue placeholder="Select stage" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEAL_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        <div className="flex items-center gap-2">
                          <Badge className={getStageBadge(stage)} variant="outline">
                            {stage.replace('_', ' ')}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="bg-amber-50 p-3 rounded-md">
                <p className="text-xs text-amber-700">
                  Stage progression: cash → supplier → transit_from → warehouse → transit_to → delivered
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageUpdateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleUpdateStage}
              disabled={updatingStage || !newStage || newStage === stageUpdateDeal?.stage}
              className="bg-[#1D2D2A] hover:bg-[#243D37]"
              data-testid="update-stage-btn"
            >
              {updatingStage ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Stage'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Investor Swap Dialog */}
      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-amber-500" />
              Swap Investor Exposure
            </DialogTitle>
          </DialogHeader>
          
          {selectedParticipation && (
            <div className="space-y-4">
              {/* Current Position Summary */}
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <h4 className="font-medium mb-2">Current Position</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-[#6C757D]">Participation:</span>
                    <span className="font-mono">{selectedParticipation.participation_number}</span>
                    <span className="text-[#6C757D]">Current Investor:</span>
                    <span>{selectedParticipation.investor_name}</span>
                    <span className="text-[#6C757D]">Outstanding:</span>
                    <span className="font-medium">{formatCurrency(selectedParticipation.principal_outstanding)}</span>
                    <span className="text-[#6C757D]">Interest Accrued:</span>
                    <span className="text-green-600">{formatCurrency(selectedParticipation.interest_accrued - (selectedParticipation.interest_paid || 0))}</span>
                    <span className="text-[#6C757D]">Current RP Rate:</span>
                    <span>{selectedParticipation.rp_rate}%</span>
                  </div>
                </CardContent>
              </Card>

              {/* New Investor Selection */}
              <div className="space-y-2">
                <Label>New Investor *</Label>
                <Select
                  value={swapData.new_investor_id}
                  onValueChange={(val) => setSwapData({ ...swapData, new_investor_id: val })}
                >
                  <SelectTrigger data-testid="swap-new-investor-select">
                    <SelectValue placeholder="Select new investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {investorBalances
                      // inactive investors get no new allocations (portfolio_status)
                      .filter(inv => inv.investor_id !== selectedParticipation.investor_id && inv.portfolio_active !== false)
                      .map((inv) => (
                        <SelectItem key={inv.investor_id} value={inv.investor_id}>
                          {inv.investor_name || inv.email} (Available: {formatCurrency(inv.available)})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Swap Amount (optional - for partial swaps) */}
              <div className="space-y-2">
                <Label>Swap Amount (leave empty for full swap)</Label>
                <Input
                  type="number"
                  placeholder={`Max: ${selectedParticipation.principal_outstanding}`}
                  value={swapData.swap_amount}
                  onChange={(e) => setSwapData({ ...swapData, swap_amount: e.target.value })}
                  data-testid="swap-amount-input"
                />
                <p className="text-xs text-[#6C757D]">
                  Leave empty to swap the entire position. Enter an amount for a partial swap.
                </p>
              </div>

              {/* New RP Rate */}
              <div className="space-y-2">
                <Label>New RP Rate (% p.a.)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder={selectedParticipation.rp_rate?.toString()}
                  value={swapData.new_rp_rate}
                  onChange={(e) => setSwapData({ ...swapData, new_rp_rate: e.target.value })}
                  data-testid="swap-rp-rate-input"
                />
                <p className="text-xs text-[#6C757D]">
                  For risk-adjusted swaps: lower rate for lower-risk-appetite investors.
                </p>
              </div>

              {/* Value Date */}
              <div className="space-y-2">
                <Label>Value Date *</Label>
                <Input
                  type="date"
                  value={swapData.value_date}
                  onChange={(e) => setSwapData({ ...swapData, value_date: e.target.value })}
                  data-testid="swap-date-input"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Portfolio rebalancing, risk transfer, etc."
                  value={swapData.notes}
                  onChange={(e) => setSwapData({ ...swapData, notes: e.target.value })}
                  data-testid="swap-notes-input"
                />
              </div>

              {/* Carry Trade Option */}
              <div className="bg-amber-50 p-4 rounded-md space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="carry-enabled"
                    checked={swapData.carry_enabled}
                    onChange={(e) => setSwapData({ ...swapData, carry_enabled: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                    data-testid="carry-enabled-checkbox"
                  />
                  <Label htmlFor="carry-enabled" className="text-sm font-medium cursor-pointer">
                    Enable Carry Trade
                  </Label>
                </div>
                
                {swapData.carry_enabled && (
                  <div className="bg-white p-3 rounded border border-amber-200 text-sm space-y-2">
                    <p className="text-amber-800">
                      <strong>Carry Rate:</strong> Original RP Rate ({Number(selectedParticipation?.rp_rate || 0).toFixed(2)}%) - New RP Rate ({swapData.new_rp_rate || selectedParticipation?.rp_rate}%) = 
                      <span className="font-bold text-amber-600 ml-1">
                        {swapData.new_rp_rate 
                          ? Math.max(0, (selectedParticipation?.rp_rate - parseFloat(swapData.new_rp_rate))).toFixed(2)
                          : '0.00'}%
                      </span>
                    </p>
                    <p className="text-xs text-[#6C757D]">
                      The original investor will earn this rate as carry on the swapped principal until the deal closes.
                    </p>
                    {swapData.new_rp_rate && parseFloat(swapData.new_rp_rate) >= selectedParticipation?.rp_rate && (
                      <p className="text-xs text-red-600 font-medium">
                        ⚠️ New RP rate must be lower than original rate for carry to apply.
                      </p>
                    )}
                  </div>
                )}
                
                {!swapData.carry_enabled && (
                  <p className="text-xs text-amber-700">
                    When enabled, the original investor earns the spread between their original RP rate and the new investor's rate.
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSwapOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSwap}
              disabled={processingSwap || !swapData.new_investor_id}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="execute-swap-btn"
            >
              {processingSwap ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <ArrowLeftRight className="h-4 w-4 mr-2" />
                  Execute Swap
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLMSPage;
